import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderSearch, TriangleAlert } from 'lucide-react';
import { Button, EmptyState, PageHeader, Spinner } from '@elbklar/ui';
import { RouterLink } from '@/components/layout/RouterLink';
import { fehlerText, isApiError } from '@/lib/api';

/**
 * Die gemeinsame Ladeansicht aller Projektseiten. Sie unterscheidet drei Fälle
 * und ersetzt dabei **nur den Inhalt**, nicht die Seite:
 *
 * 1. **lädt** — ein Dreher an der Stelle des Inhalts,
 * 2. **nicht gefunden** (404, oder gar keine ID in der Adresse) — „Projekt
 *    nicht gefunden“ mit dem Weg zurück zu „Meine Arbeit“,
 * 3. **Ladefehler** — eigene Aussage mit dem Text des Servers und dem Knopf
 *    „Erneut versuchen“.
 *
 * Der Unterschied zwischen 2 und 3 ist der Grund, warum es diese Komponente
 * gibt: Vorher verkaufte jede Projektseite jeden Ladefehler als „Projekt nicht
 * gefunden“ beziehungsweise „Daten nicht gefunden“ — eine inhaltliche Aussage
 * über ein Projekt, das es sehr wohl gibt, und ohne jeden Weg zurück. Ein
 * 500er oder ein abgebrochenes Netz sieht hier jetzt anders aus als ein
 * gelöschtes Projekt, und man kann es nochmal versuchen.
 *
 * ## Verwendung
 *
 * Die Seite baut ihren Kopf, sobald das Projekt da ist, und gibt ihn mit. So
 * bleiben Titel, Status und Reiterleiste stehen, während ein Reiter seine
 * eigenen Daten nachlädt — man weiß beim Wechsel die ganze Zeit, wo man ist:
 *
 * ```tsx
 * const { data: project, isLoading, isFetching, error } = useQuery(…);
 * const { data: kalkulation, …: kLoading, …: kFetching, error: kError } = useQuery(…);
 *
 * // Der Kopf steht, sobald das Projekt da ist — auch wenn der Reiter noch lädt.
 * const kopf = project ? <ProjektKopf project={project} untertitel="Kalkulation" /> : undefined;
 *
 * if (!project || !kalkulation) {
 *   return (
 *     <ProjektLaden
 *       kopf={kopf}
 *       laedt={isLoading || isFetching || kLoading || kFetching}
 *       fehler={error ?? kError}
 *       onErneut={() => { refetch(); kRefetch(); }}
 *     />
 *   );
 * }
 * ```
 *
 * `laedt` nimmt bewusst auch `isFetching` mit: Nach einem Fehler hält
 * react-query `isLoading` auf `false`, und ohne diesen Zusatz täte der Knopf
 * „Erneut versuchen“ sichtbar nichts (er zeigt den Lauf selbst an).
 *
 * `fehler` ist absichtlich `unknown` und nicht `Error`: Es kommt direkt aus
 * `useQuery(...).error` und wird mit `fehlerText` gelesen, damit der Text des
 * Servers (`detail`) auf dem Bildschirm steht und nicht „Request failed with
 * status code 500“.
 *
 * Die Schnittstelle bleibt absichtlich klein (vier Angaben, keine Kinder):
 * Andere Pakete setzen darauf auf; eine Variante, die den Inhalt als `children`
 * nimmt, würde ihn auswerten, bevor die Daten da sind — genau dort stürzt eine
 * Seite ab, die `kalkulation.zonen` liest.
 */
export interface ProjektLadenProps {
  /**
   * Der Projektkopf der Seite, sobald das Projekt geladen ist. Ohne Angabe
   * steht ein schlichter Kopf „Projekt“ mit dem Weg zurück da, damit die Seite
   * nicht randlos beginnt.
   */
  kopf?: ReactNode;
  /** Irgendeine Abfrage der Seite lädt oder lädt neu. */
  laedt?: boolean;
  /** Fehler einer Abfrage der Seite. 404 heißt „nicht gefunden“. */
  fehler?: unknown;
  /** Lädt die Abfragen der Seite erneut. Ohne Angabe gibt es den Knopf nicht. */
  onErneut?: () => void;
}

/** Ein 404 ist eine Aussage über das Projekt, jeder andere Fehler nicht. */
function istNichtGefunden(fehler: unknown): boolean {
  return isApiError(fehler) && fehler.response?.status === 404;
}

export function ProjektLaden({ kopf, laedt, fehler, onErneut }: ProjektLadenProps) {
  const navigate = useNavigate();

  const zurueck = (
    <Button size="lg" onClick={() => navigate('/')}>
      Zurück zu Meine Arbeit
    </Button>
  );

  let inhalt: ReactNode;
  if (fehler && !istNichtGefunden(fehler)) {
    inhalt = (
      <EmptyState
        icon={TriangleAlert}
        title="Projekt ließ sich nicht laden"
        description={fehlerText(fehler, 'Das Projekt ist gerade nicht erreichbar. Erneut versuchen.')}
        action={
          onErneut ? (
            <Button size="lg" onClick={onErneut} loading={laedt}>
              Erneut versuchen
            </Button>
          ) : (
            zurueck
          )
        }
      />
    );
  } else if (laedt) {
    inhalt = (
      <div className="flex h-64 items-center justify-center">
        <Spinner size={24} label="Projekt wird geladen" />
      </div>
    );
  } else {
    inhalt = (
      <EmptyState
        icon={FolderSearch}
        title="Projekt nicht gefunden"
        description="Das gesuchte Projekt existiert nicht oder wurde gelöscht."
        action={zurueck}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6 max-md:gap-4">
      {/* Der Ersatzkopf trägt den Weg zurück statt `SidebarToggle`: Er steht
          genau dann da, wenn das Projekt fehlt, und dann hilft „Zurück zu
          Meine Arbeit“ mehr als das Ein- und Ausklappen der Seitenleiste. Und
          `SidebarToggle` verlangt eine `AppShell` um sich herum (sonst wirft
          `useAppShell`) — diese Ansicht soll in jeder Umgebung rendern. */}
      {kopf ?? (
        <PageHeader
          title="Projekt"
          zurueck={{ label: 'Zurück zu Meine Arbeit', href: '/', linkAs: RouterLink }}
        />
      )}
      {inhalt}
    </div>
  );
}

export default ProjektLaden;
